import React, { useEffect, useRef, useState } from "react";
import TaxInvoiceDetailsForm from "./TaxInvoiceDetailsForm";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Check, CircleX, Clock, FileText, Info, Lock, Pencil, Upload } from "lucide-react";
import { Button } from "../ui";
import {
  cancelBusinessSubmission,
  downloadBusinessDocument,
  submitBusinessProfile,
} from "../../services/accountService";

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = new Set(["pdf", "png", "jpg", "jpeg"]);

function formatSize(bytes) {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  const mb = size / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
}

function formatStatusDate(value, locale) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(locale || undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function extensionOf(name) {
  const parts = String(name || "").toLowerCase().split(".");
  return parts.length > 1 ? parts.pop() : "";
}

function emptyForm() {
  return {
    crNumber: "",
    legalTradeName: "",
    registeredAddress: "",
  };
}

function filesFromProfile(profile) {
  return (profile?.form?.documents || []).map((doc) => ({
    key: doc.id,
    id: doc.id,
    name: doc.originalName,
    size: doc.size,
    existing: true,
  }));
}

function approvedSnapshot(profile) {
  if (profile?.approvedProfile) return profile.approvedProfile;
  if (profile?.profileStatus === "approved") {
    return {
      crNumber: profile.form?.crNumber || "",
      legalTradeName: profile.form?.legalTradeName || "",
      registeredAddress: profile.form?.registeredAddress || "",
      documents: profile.form?.documents || [],
    };
  }
  return null;
}

function ApprovedMark() {
  return (
    <svg className="my-account-biz-approved-mark" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="8" fill="#16A34A" />
      <path
        d="M4.4 8.15 6.7 10.45 11.6 5.55"
        fill="none"
        stroke="#fff"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockedField({ label, value }) {
  return (
    <div className="my-account-biz-col">
      <span className="my-account-field__label">{label}</span>
      <div className="my-account-biz-locked-value">
        <span>{value || "—"}</span>
        <Lock size={14} strokeWidth={1.5} aria-hidden="true" />
      </div>
    </div>
  );
}

function BusinessProfileForm({ profile, businessInfo, profileExists = false, onSubmitted, onTaxSaved }) {
  const { t, i18n } = useTranslation("translation");
  const ta = (key, opts) => t(`myAccount.${key}`, opts);
  const fileInputRef = useRef(null);
  const taxFormRef = useRef(null);
  const [fields, setFields] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(false);

  const status = profile?.profileStatus || "unsubmitted";
  const snapshot = approvedSnapshot(profile);
  const openRequest = status === "pending" || status === "update_pending" || status === "rejected";
  const showEditor = status !== "approved" || editingUpdate;
  const showTwoBlocks = Boolean(snapshot) && showEditor;
  const busy = submitting || cancelling;

  useEffect(() => {
    const form = profile?.form || emptyForm();
    setFields({
      crNumber: form.crNumber || "",
      legalTradeName: form.legalTradeName || "",
      registeredAddress: form.registeredAddress || "",
    });
    setFiles(filesFromProfile(profile));
    setErrors({});
    if (profile?.profileStatus !== "approved") {
      setEditingUpdate(false);
    }
  }, [profile]);

  const reset = () => {
    const form = profile?.form || emptyForm();
    setFields({
      crNumber: form.crNumber || "",
      legalTradeName: form.legalTradeName || "",
      registeredAddress: form.registeredAddress || "",
    });
    setFiles(filesFromProfile(profile));
    setErrors({});
  };

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const next = [...files];
    let error = "";
    incoming.forEach((file) => {
      const ext = extensionOf(file.name);
      if (!ALLOWED.has(ext)) {
        error = ta("fileType");
        return;
      }
      if (file.size > MAX_BYTES) {
        error = ta("fileTooLarge");
        return;
      }
      if (next.length >= MAX_FILES) {
        error = ta("fileTooMany");
        return;
      }
      next.push({
        key: `${file.name}-${file.size}-${file.lastModified}-${next.length}`,
        name: file.name,
        size: file.size,
        existing: false,
        file,
      });
    });
    setFiles(next);
    if (error) toast.error(error);
  };

  const validate = () => {
    const next = {};
    if (!fields.crNumber.trim()) next.crNumber = ta("crRequired");
    if (!fields.legalTradeName.trim()) next.legalTradeName = ta("nameRequired");
    if (!fields.registeredAddress.trim()) next.registeredAddress = ta("addressRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!validate()) return;
    if (taxFormRef.current && !taxFormRef.current.validateTax()) {
      return;
    }
    setSubmitting(true);
    try {
      const body = new FormData();
      body.append("crNumber", fields.crNumber.trim());
      body.append("legalTradeName", fields.legalTradeName.trim());
      body.append("registeredAddress", fields.registeredAddress.trim());
      body.append(
        "keepDocumentIds",
        JSON.stringify(files.filter((file) => file.existing && file.id).map((file) => file.id))
      );
      files
        .filter((file) => !file.existing && file.file)
        .forEach((file) => body.append("documents", file.file));
      const data = await submitBusinessProfile(body);
      if (taxFormRef.current) {
        const taxSaved = await taxFormRef.current.saveTaxDetails({
          toastOnSuccess: false,
          requireProfile: false,
        });
        if (!taxSaved) return;
      }
      toast.success(snapshot ? ta("requestChangesSuccess") : ta("submitSuccess"));
      onSubmitted?.(data);
    } catch (err) {
      toast.error(err.response?.data?.message || ta("submitError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (busy) return;
    if (!openRequest) {
      reset();
      setEditingUpdate(false);
      return;
    }
    setCancelling(true);
    try {
      const data = await cancelBusinessSubmission();
      toast.success(ta("cancelRequestSuccess"));
      setEditingUpdate(false);
      onSubmitted?.(data);
    } catch (err) {
      toast.error(err.response?.data?.message || ta("cancelRequestError"));
    } finally {
      setCancelling(false);
    }
  };

  const downloadExisting = async (file) => {
    if (!file?.id) return;
    try {
      const response = await downloadBusinessDocument(file.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || ta("downloadDocumentError"));
    }
  };

  const updatedDate = formatStatusDate(profile?.updatedAt || profile?.approvedAt, i18n.language);
  const submittedDate = formatStatusDate(profile?.submittedAt, i18n.language);

  const previousValue = (key) => String(snapshot?.[key] || "").trim();
  const fieldChanged = (key) => Boolean(snapshot) && String(fields[key] || "").trim() !== previousValue(key);

  const renderWas = (key) =>
    fieldChanged(key) ? (
      <p className="my-account-biz-was">
        <span>{ta("was")}</span>
        <s>{previousValue(key) || "—"}</s>
      </p>
    ) : null;

  const editorTitle =
    status === "rejected" || status === "pending" || status === "update_pending"
      ? ta("submittedForApproval")
      : ta("updateBusinessInfo");

  const handleUpdateBusinessAndTax = async () => {
    if (busy) return;
    if (profileExists && taxFormRef.current) {
      const taxSaved = await taxFormRef.current.saveTaxDetails();
      if (!taxSaved) return;
    }
    setEditingUpdate(true);
  };

  const taxSection = (
    <TaxInvoiceDetailsForm
      ref={taxFormRef}
      embedded
      hideFooter
      businessInfo={businessInfo}
      profileExists={profileExists}
      inputsEnabled={profileExists || showEditor}
      onSaved={onTaxSaved}
    />
  );

  return (
    <form className="my-account-biz-card" onSubmit={handleSubmit}>
      {snapshot ? (
        <div className="my-account-biz-locked">
          {showTwoBlocks ? (
            <div className="my-account-biz-subhead">
              <div className="my-account-biz-subhead__lead">
                <ApprovedMark />
                <span className="my-account-biz-subhead__title">{ta("currentlyApproved")}</span>
              </div>
              <span className="my-account-biz-pill my-account-biz-pill--approved">{ta("statusApproved")}</span>
            </div>
          ) : null}
          <div className="my-account-biz-grid">
            <LockedField label={ta("fields.crNumber")} value={snapshot.crNumber} />
            <LockedField label={ta("fields.legalTradeName")} value={snapshot.legalTradeName} />
            <LockedField label={ta("fields.registeredAddress")} value={snapshot.registeredAddress} />
          </div>
          <div className="my-account-biz-docs">
            <span className="my-account-field__label">{ta("approvedDocuments")}</span>
            {(snapshot.documents || []).length ? (
              <ul className="my-account-file-list">
                {snapshot.documents.map((file) => (
                  <li key={file.id} className="my-account-file-chip my-account-file-chip--locked">
                    <FileText size={14} strokeWidth={1.5} aria-hidden="true" />
                    <button
                      type="button"
                      className="my-account-file-name"
                      onClick={() =>
                        downloadExisting({
                          id: file.id,
                          name: file.originalName,
                        })
                      }
                    >
                      {file.originalName}
                    </button>
                    <span className="my-account-file-size">· {formatSize(file.size)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="my-account-docs__hint">{ta("noApprovedDocuments")}</p>
            )}
          </div>
        </div>
      ) : null}

      {showEditor ? (
        <div className={`my-account-biz-editor${showTwoBlocks ? " my-account-biz-editor--split" : ""}`}>
          {status !== "unsubmitted" ? (
            <div className="my-account-biz-subhead">
              <div className="my-account-biz-subhead__lead">
                {status === "rejected" ? (
                  <CircleX size={16} className="my-account-biz-icon--rejected" aria-hidden="true" />
                ) : status === "pending" || status === "update_pending" ? (
                  <Clock size={16} className="my-account-biz-icon--pending" aria-hidden="true" />
                ) : (
                  <Pencil size={16} strokeWidth={2} className="my-account-biz-icon--edit" aria-hidden="true" />
                )}
                <div>
                  <div className="my-account-biz-subhead__title">{editorTitle}</div>
                  <p className="my-account-biz-subhead__hint">
                    {status === "approved"
                      ? ta("updateBusinessInfoHint")
                      : ta("awaitingReview")}
                  </p>
                </div>
              </div>
              {status === "pending" || status === "update_pending" ? (
                <span className="my-account-biz-pill my-account-biz-pill--pending">{ta("statusPending")}</span>
              ) : null}
              {status === "rejected" ? (
                <span className="my-account-biz-pill my-account-biz-pill--rejected">{ta("statusRejected")}</span>
              ) : null}
            </div>
          ) : null}

          {status === "rejected" && profile?.rejectionReason ? (
            <div className="my-account-rejection" role="alert">
              <Info size={17} aria-hidden="true" />
              <div>
                <p className="my-account-rejection__title">{ta("reasonForRejection")}</p>
                <p>{profile.rejectionReason}</p>
              </div>
            </div>
          ) : null}

          <div className="my-account-biz-grid">
            <label className="my-account-biz-col">
              <span className="my-account-field__label">{ta("fields.crNumber")}</span>
              <input
                className={`my-account-biz-input${fieldChanged("crNumber") ? " is-changed" : ""}${errors.crNumber ? " is-invalid" : ""}`}
                value={fields.crNumber}
                onChange={(e) => setFields((prev) => ({ ...prev, crNumber: e.target.value }))}
              />
              {renderWas("crNumber")}
              {errors.crNumber ? <span className="my-account-field-error">{errors.crNumber}</span> : null}
            </label>
            <label className="my-account-biz-col">
              <span className="my-account-field__label">{ta("fields.legalTradeName")}</span>
              <input
                className={`my-account-biz-input${fieldChanged("legalTradeName") ? " is-changed" : ""}${errors.legalTradeName ? " is-invalid" : ""}`}
                value={fields.legalTradeName}
                onChange={(e) => setFields((prev) => ({ ...prev, legalTradeName: e.target.value }))}
              />
              {renderWas("legalTradeName")}
              {errors.legalTradeName ? (
                <span className="my-account-field-error">{errors.legalTradeName}</span>
              ) : null}
            </label>
            <label className="my-account-biz-col">
              <span className="my-account-field__label">{ta("fields.registeredAddress")}</span>
              <input
                className={`my-account-biz-input${fieldChanged("registeredAddress") ? " is-changed" : ""}${errors.registeredAddress ? " is-invalid" : ""}`}
                value={fields.registeredAddress}
                onChange={(e) =>
                  setFields((prev) => ({ ...prev, registeredAddress: e.target.value }))
                }
              />
              {renderWas("registeredAddress")}
              {errors.registeredAddress ? (
                <span className="my-account-field-error">{errors.registeredAddress}</span>
              ) : null}
            </label>
          </div>

          <div className="my-account-biz-docs">
            <span className="my-account-field__label">{ta("documentsLabel")}</span>
            <div
              className={`my-account-dropzone${dragOver ? " is-dragover" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
            >
              <Upload size={17} aria-hidden="true" />
              <button
                type="button"
                className="my-account-choose-file"
                onClick={() => fileInputRef.current?.click()}
              >
                {ta("chooseFile")}
              </button>
              <span>{ta("dragFiles")}</span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
            {files.length ? (
              <ul className="my-account-file-list">
                {files.map((file) => (
                  <li key={file.key} className="my-account-file-chip">
                    <FileText size={14} strokeWidth={1.5} aria-hidden="true" />
                    {file.existing ? (
                      <button type="button" className="my-account-file-name" onClick={() => downloadExisting(file)}>
                        {file.name}
                      </button>
                    ) : (
                      <span className="my-account-file-name">{file.name}</span>
                    )}
                    <span className="my-account-file-size">· {formatSize(file.size)}</span>
                    <button
                      type="button"
                      className="my-account-file-remove"
                      aria-label={ta("removeFile")}
                      onClick={() => setFiles((prev) => prev.filter((item) => item.key !== file.key))}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

        </div>
      ) : null}

      {taxSection}

      {!showEditor && snapshot ? (
        <div className="my-account-biz-locked my-account-biz-card-footer">
          <div className="my-account-biz-footer">
            <span className="my-account-biz-status my-account-biz-status--approved">
              <Check size={16} strokeWidth={2.5} aria-hidden="true" />
              {updatedDate ? ta("updatedOn", { date: updatedDate }) : ta("statusApproved")}
            </span>
            <div className="my-account-biz-actions">
              <Button color="dark" type="button" onClick={handleUpdateBusinessAndTax} disabled={busy}>
                {ta("updateBusinessAndTaxInformation")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {showEditor ? (
        <div className="my-account-biz-locked my-account-biz-card-footer">
          <div className={`my-account-biz-footer${openRequest ? "" : " my-account-biz-footer--end"}`}>
            {status === "rejected" ? (
              <span className="my-account-biz-status my-account-biz-status--rejected">
                <Clock size={16} aria-hidden="true" />
                {ta("notApproved")}
              </span>
            ) : null}
            {status === "pending" || status === "update_pending" ? (
              <span className="my-account-biz-status my-account-biz-status--pending">
                <Clock size={16} aria-hidden="true" />
                {submittedDate ? ta("submittedOn", { date: submittedDate }) : ta("statusPending")}
              </span>
            ) : null}
            <div className="my-account-biz-actions">
              <Button
                className="my-account-cancel"
                color="light"
                type="button"
                onClick={handleCancel}
                disabled={busy}
              >
                {cancelling ? ta("submitting") : openRequest ? ta("cancelRequest") : ta("cancel")}
              </Button>
              <Button color="dark" type="submit" disabled={busy}>
                {submitting ? ta("submitting") : openRequest ? ta("resubmit") : ta("submitForApproval")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </form>
  );
}

export default BusinessProfileForm;
